<template>
	<div>
		<div class="row gutter-sm q-mb-lg">
			<div class="col-xs-12 col-sm-12">
				<q-field :count="256">
					<q-input
						v-model="title"
						ref="title"
						:float-label="$t('title')"
						:error="$v.title.$error"
						@input="$v.title.$touch"
						:disable="!manage_trails"
					/>
				</q-field>
			</div>

			<div class="col-xs-12 col-sm-12">
				<q-field :count="1024">
					<q-input
						v-model="description"
						ref="description"
						:float-label="$t('introduction')"
						rows="1"
						:max-height="100"
						type="textarea"
						:error="$v.description.$error"
						@input="$v.description.$touch"
						:disable="!manage_trails"
					/>
				</q-field>
			</div>

			<div class="col-xs-12 col-sm-6">
				<q-select
					v-model="trail_parent"
					:options="selected_parent_trail"
					:float-label="$t('requirement')"
					@click.native="showTrailParents"
					@keyup.enter.native="showTrailParents"
					chips
					clearable
					@clear="clearSelection"
					:readonly="!selected_parent_trail"
					:disable="!manage_trails"
				/>
			</div>

			<div class="col-xs-12 col-sm-6">
				<q-datetime
					v-model="availability_date"
					:float-label="$t('availability_date')"
					type="date"
					format="DD/MM/YYYY"
					clearable
				/>
			</div>

			<div class="col-xs-12 col-sm-12" v-if="showCategories">
				<q-select
					v-model="category"
					ref="category"
					filter
					autofocus-filter
					:float-label="$t('categories')"
					:options="optionCategories"
					:error="$v.category.$error"
					@input="$v.category.$touch"
					/>
			</div>

			<div class="col-xs-12 col-sm-12">
				<q-select
					v-model="tag"
					ref="tag"
					:options="optionTags"
					:float-label="$t('tags')"
					:error="$v.tag.$error"
					@input="$v.tag.$touch"
					multiple
					chips
					filter
					autofocus-filter
					:disable="!manage_trails"
				/>
			</div>

			<div class="col-xs-12 col-sm-12">
				<div class="row gutter-sm">
					<div class="col-xs-12 col-sm-6">
						<p class="caption">{{ $t('image') }}</p>
						<uploader
							content_type="images"
							@fileUploaded="imageUploaded"
							:multiple="false"
							:cropper="true"
							:disable="!manage_trails"
						/>

						<p v-if="image_url" class="q-mt-md file-url">
							<q-icon name="fas fa-check" color="positive" />
							<a :href="image_url" target="_blank"> {{ $truncateLink(image_url) }}</a>
						</p>
					</div>
					<div class="col-xs-12 col-sm-6">
						<p class="caption">
							{{ $t('alt_image') }}
							<q-icon
								name="far fa-question-circle"
								size="1.5rem"
								color="grey"
								class="q-ml-sm q-pb-sm"
							>
								<q-tooltip
									class="custom-tooltip"
									anchor="top left"
									self="bottom right"
								>
									{{ $t('alt_image_information_helper') }}
								</q-tooltip>
							</q-icon>
						</p>
						<div class="flex justify-between">
							<template v-for="lang in languages" class="row flex">
								<q-field :count="170" :key="lang.id" v-if="lang.code === 'pt-br'">
									<q-input
										v-model="language_br"
										:float-label="$t(`${lang.name}`)"
										ref="language_br"
										:error="$v.language_br.$error"
										@input="$v.language_br.$touch"
									/>
								</q-field>
								<q-field :count="170" :key="lang.id" v-if="lang.code === 'en-us'">
									<q-input
										v-model="language_us"
										:float-label="$t(`${lang.name}`)"
										ref="language_us"
										:error="$v.language_us.$error"
										@input="$v.language_us.$touch"
									/>
								</q-field>
								<q-field :count="170" :key="lang.id" v-if="lang.code === 'es-es'">
									<q-input
										v-model="language_es"
										:float-label="$t(`${lang.name}`)"
										ref="language_es"
										:error="$v.language_es.$error"
										@input="$v.language_es.$touch"
									/>
								</q-field>
								<q-field :count="170" :key="lang.id" v-if="lang.code === 'fr-fr'">
									<q-input
										v-model="language_fr"
										:float-label="$t(`${lang.name}`)"
										ref="language_fr"
										:error="$v.language_fr.$error"
										@input="$v.language_fr.$touch"
									/>
								</q-field>
							</template>
						</div>
					</div>
				</div>
			</div>
		</div>

		<q-tabs v-model="tab" color="secondary" class="shadow-1">
			<q-tab
				slot="title"
				name="permissions"
				icon="fas fa-clipboard-check"
				:label="$t('permissions')"
				:disable="!manage_trails"
			/>

			<q-tab-pane name="permissions" class="q-px-lg q-pb-xl">
				<div class="row gutter-sm">
					<div class="col-xs-12 col-sm-12">
						<p class="caption">{{ $t('areas') }}</p>
						<q-checkbox
							class="q-mb-sm"
							:disable="!manage_trails"
							v-model="all_areas"
							:val="true"
							:label="$t('select_all_areas')"
							@input="checkAllAreas()"
						/>
						<select-multiple
							label=""
							:filters="area"
							:options="areas"
							@on_value="area = $event"
							:error="$v.area.$error"
							:input="changeAreas"
							:disable="!manage_trails"
						/>
					</div>

					<div class="col-xs-12 col-sm-12">
						<p class="caption">{{ $t('profiles') }}</p>

						<div class="row gutter-sm">
							<div class="col-xs-12 col-sm-12">
								<q-checkbox
									v-model="all_profiles"
									:val="true"
									:label="$t('all')"
									@input="checkAll()"
									:disable="!manage_trails"
								/>
							</div>

							<div
								class="col-xs-12 col-sm-4"
								ref="profile"
								v-for="(item, index) in profiles"
								:key="index"
								:class="getErrorClass($v.profile.$error)"
							>
								<q-checkbox
									keep-color
									v-model="profile"
									:val="item.value"
									:label="item.label"
									@input="changeProfiles()"
									:color="getErrorColor($v.profile.$error)"
									:disable="!manage_trails"
								/>
							</div>
						</div>
					</div>
				</div>

				<div class="row gutter-sm q-mt-md" v-if="useTrailSubscription">
					<div class="col-xs-12 col-sm-12">
						<p class="caption">{{ $t('mandatory_areas') }}</p>
						<q-checkbox
							class="q-mb-sm"
							:disable="!manage_trails"
							v-model="all_mandatory_areas"
							:val="true"
							:label="$t('select_all_areas')"
							@input="checkAllMandatoryAreas()"
						/>
						<select-multiple
							label=""
							:filters="mandatory_areas"
							:options="mandatoryAreas"
							@on_value="mandatory_areas = $event"
							:error="$v.mandatory_areas.$error"
							:input="$v.mandatory_areas.$touch"
							:disable="!manage_trails"
						/>
					</div>

					<div class="col-xs-12 col-sm-12">
						<p class="caption">{{ $t('mandatory_profiles') }}</p>

						<div class="row gutter-sm">
							<div class="col-xs-12 col-sm-12">
								<q-checkbox
									v-model="all_mandatory_profiles"
									:val="true"
									:label="$t('all')"
									@input="checkAllMandatoryProfiles()"
									:disable="!manage_trails"
								/>
							</div>

							<div
								class="col-xs-12 col-sm-4"
								v-for="(item, index) in mandatoryProfiles"
								:key="index"
							>
								<q-checkbox
									keep-color
									v-model="mandatory_profiles"
									ref="mandatory_profiles"
									:val="item.value"
									:label="item.label"
									:error="$v.mandatory_profiles.$error"
									@input="$v.mandatory_profiles.$touch"
									:disable="!manage_trails"
								/>
							</div>
						</div>
					</div>
				</div>

				<div class="row gutter-sm">
					<div class="col-xs-12 col-sm-12">
						<q-select
							v-model="user_group"
							ref="user_group"
							:options="user_groups"
							:float-label="$t('groups')"
							:error="$v.user_group.$error"
							@input="$v.user_group.$touch"
							multiple
							chips
							:disable="!manage_trails"
						/>
					</div>
				</div>

				<div class="row gutter-sm q-my-sm" v-if="id_trail != 0">
					<div class="col-xs-12 col-sm-11">
						<q-input
							v-model="url_trail_portal"
							:float-label="$t('link')"
							:disable="true"
						/>
					</div>

					<div class="col-xs-12 col-sm-1 q-mt-lg">
						<q-btn
							dense
							color="primary"
							icon="far fa-copy"
							class="full-width"
							@click="copyLink()"
						>
							<q-tooltip>
								{{ $t('copy_link') }}
							</q-tooltip>
						</q-btn>
					</div>
				</div>
			</q-tab-pane>
		</q-tabs>

		<div class="row gutter-sm justify-end q-mt-lg">
			<div class="col-xs-12 col-sm-3" :class="getOrder('cancel')">
				<q-btn
					color="tertiary"
					class="full-width"
					:label="$t('back')"
					icon="fas fa-chevron-left"
					@click="$router.push({ name: 'admin_trails' })"
				/>
			</div>

			<div class="col-xs-12 col-sm-3" :class="getOrder('save')">
				<q-btn
					color="secondary"
					class="full-width"
					:label="$t('save')"
					icon="far fa-save"
					@click="saveData()"
					:disable="!manage_trails"
				/>
			</div>
		</div>

		<q-modal
			@hide="filter_parent_trail = ''"
			v-model="show_add_trail_parent"
			:content-css="{ minWidth: getMinWidth, maxWidth: getMaxWidth, minHeight: '80vh' }"
		>
			<q-modal-layout>
				<q-toolbar slot="header">
					<q-btn
						flat
						round
						dense
						@click="show_add_trail_parent = false"
						icon="fas fa-times"
					/>
					<q-toolbar-title>
						{{ $t('trails') }}
					</q-toolbar-title>
				</q-toolbar>

				<div class="layout-padding">
					<q-table
						:data="filteredParentTrails"
						:columns="columns"
						:pagination="parent_pagination"
						@request="fetchTrailParents"
						row-key="content_id"
						:no-data-label="$t('no_data_avaliable')"
						:no-results-label="$t('no_results_found')"
						:loading-label="$t('loading_data')"
						:rows-per-page-label="$t('items_per_page')"
						:rows-per-page-options="[0]"
						binary-state-sort
					>
						<template slot="top" slot-scope="props">
							<q-search
								v-model="filter_parent_trail"
								icon="fas fa-search"
								:placeholder="$t('search')"
								:float-label="$t('search')"
								class="q-mr-lg"
								:debounce="300"
							/>

							<div class="col" />
						</template>

						<q-tr slot="header" slot-scope="props" :props="props">
							<q-th v-for="col in props.cols" :key="col.name" :props="props">
								{{ $t(col.label) }}
							</q-th>
						</q-tr>

						<q-tr slot="body" slot-scope="props" :props="props">
							<q-td key="content_id" :props="props" auto-width>
								{{ props.row.value }}
							</q-td>

							<q-td key="content_label" :props="props" auto-width>
								{{ props.row.label }}
							</q-td>

							<q-td key="actions" :props="props" auto-width>
								<q-btn
									color="secondary"
									icon="far fa-hand-pointer"
									:label="$t('select')"
									@click="selectParentTrail(props.row.value)"
									:disable="!manage_trails"
								/>
							</q-td>
						</q-tr>
					</q-table>
				</div>
			</q-modal-layout>
		</q-modal>
	</div>
</template>

<script>
import Uploader from 'components/Uploader'
import { mapGetters } from 'vuex'
import { required, requiredIf, maxLength } from 'vuelidate/lib/validators'
import { date } from 'quasar'
const { formatDate } = date
import * as moment from 'moment'
import * as tz from 'moment-timezone'
import SelectMultiple from 'components/utils/SelectMultiple'
export default {
	i18nOptions: { namespaces: ['custom', 'common', 'trails', 'admin', 'users'] },

	props: ['manage_trails'],

	data() {
		return {
			language_br: '',
			language_us: '',
			language_es: '',
			language_fr: '',
			all_areas: false,
			all_mandatory_areas: false,
			all_mandatory_profiles: false,
			all_profiles: false,
			area: [],
			availability_date: '',
			description: '',
			id_trail: '',
			image_url: '',
			mandatory_areas: [],
			mandatory_profiles: [],
			profile: [],
			tab: 'permissions',
			tag: [],
			title: '',
			trail_parent: '',
			trail_parents: [],
			user_group: [],
			show_add_trail_parent: false,
			selected_parent_trail: [],
			filter_parent_trail: '',
			category: '',
			parent_pagination: {
				page: 1,
				rowsNumber: 50,
				rowsPerPage: 50,
				descending: false,
				sortBy: 'hierarchy_order',
			},
			columns: [
				{
					name: 'content_id',
					required: true,
					label: '#',
					align: 'left',
					field: 'value',
					sortable: false,
				},
				{
					name: 'content_label',
					required: true,
					label: 'trails',
					align: 'left',
					field: 'label',
					sortable: false,
				},
				{
					name: 'actions',
					required: true,
					label: '',
					align: 'right',
					field: 'actions',
					sortable: false,
				},
			],
		}
	},

	components: {
		uploader: Uploader,
		'select-multiple': SelectMultiple,
	},

	computed: {
		...mapGetters({
			access_token: 'common/access_token',
			areas: 'registration_lists/country_areas',
			categories: 'registration_lists/trail_complete_categories',
			clients: 'params/clients',
			id_user_client_selected: 'common/id_user_client_selected',
			profiles: 'registration_lists/profiles',
			tags: 'registration_lists/tags',
			user_groups: 'registration_lists/group_users',
			languages: 'params/languages',
		}),

		getHeader() {
			return {
				headers: {
					'content-type': 'application/json',
					authorization:
						'Basic ' +
						btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
					authorization: 'Bearer ' + this.access_token,
				},
			}
		},

		optionTags() {
			if (this.tags.length > 0) {
				return this.tags.map((t) => {
					let areas = t.areas.map((a) => a.name)
					return {
						value: t.value,
						label: t.label,
						sublabel: areas.join(', '),
					}
				})
			} else return []
		},

		useTrailSubscription() {
			let client = this.clients.find((obj) => obj.id === this.id_user_client_selected)
			return client ? client.use_trail_subscription : false
		},

		mandatoryAreas() {
			return this.areas.filter((e) => this.area.includes(e.value))
		},

		mandatoryProfiles() {
			return this.profiles.filter((e) => this.profile.includes(e.value))
		},

		getMaxWidth() {
			if (this.$q.platform.is.mobile) return '100vw'
			else return '90vw'
		},

		getMinWidth() {
			if (!this.$q.platform.is.mobile) return '90vw'
		},

		filteredParentTrails() {
			let matches = []
			if (this.filter_parent_trail.length) {
				const term = this.filter_parent_trail

				matches = this.trail_parents.filter(
					(t) => t.label.includes(term) || t.value.toString().includes(term)
				)
			}
			return matches && matches.length ? matches : this.trail_parents
		},

		url_trail_portal() {
			let url = process.env.SITE_URL ? process.env.SITE_URL : document.location.origin
			return `${url}/trails/${this.id_trail}`
		},

		optionCategories() {
			if (this.categories.length > 0) {
				return this.categories.map((c) => {
					return {
						value: c.value,
						label: c.label,
					}
				})
			} else return []
		},

		showCategories() {
			let obj = this.clients.find((obj) => {
				return obj.id == this.id_user_client_selected
			})
			if (obj) return obj.has_trail_category
			else return false
		},
	},

	methods: {
		saveData() {
			this.$v.$touch()

			if (!this.$v.$error) {
				this.$q.loading.show()

				let data = {
					areas: this.area,
					description: this.description,
					mandatory_areas: this.mandatory_areas.filter((e) => this.area.includes(e)),
					mandatory_profiles: this.mandatory_profiles.filter((e) =>
						this.profile.includes(e)
					),
					profiles: this.profile,
					trail_complete_category: this.category,
					tags: this.tag,
					title: this.title,
					trail_image: this.image_url,
					trail_parent: this.trail_parent,
					user_groups: this.user_group,
					availability_date: this.availability_date
						? date.formatDate(this.availability_date, 'YYYY-MM-DD')
						: null,
					trail_image_alt_texts: {
						'pt-br': this.language_br ? this.language_br : '',
						'en-us': this.language_us ? this.language_us : '',
						'es-es': this.language_es ? this.language_es : '',
						'fr-fr': this.language_fr ? this.language_fr : '',
					},
				}

				if (this.id_trail != 0) {
					this.$axios
						.put(`/admin_trails/trails/${this.id_trail}/`, data, this.getHeader)
						.then(() => {
							this.$q.notify({
								message: this.$t('basic_data_save_success'),
								type: 'positive',
								icon: 'far fa-save',
							})
						})
						.catch((e) => {
							this.$q.notify({
								message: this.$t('basic_data_save_error'),
								type: 'negative',
								icon: 'fas fa-ban',
							})

							console.log(e)
						})
						.finally(() => {
							this.$q.loading.hide()
						})
				} else {
					this.$axios
						.post(`/admin_trails/trails/`, data, this.getHeader)
						.then((response) => {
							this.id_trail = response.data.id
							this.$router.push({
								name: 'admin_trail',
								params: { id: response.data.id },
							})

							this.$q.notify({
								message: this.$t('basic_data_save_success'),
								type: 'positive',
								icon: 'far fa-save',
							})
						})
						.catch((e) => {
							console.log(e)
						})
						.finally(() => {
							this.$q.loading.hide()
						})
				}
			} else {
				this.$formInputErrorFocus(this.$v, this.$refs)

				this.$q.notify({
					message: this.$t('make_sure_that_all_fields_are_filled_in'),
					type: 'negative',
					icon: 'fas fa-exclamation-triangle',
					position: 'top',
				})
			}
		},

		fetchData() {
			this.$axios
				.get(`/admin_trails/trails/${this.id_trail}/`, this.getHeader)
				.then((response) => {
					this.title = response.data.title
					this.description = response.data.description
					if (response.data.trail_parent)
						this.selectParentTrail(response.data.trail_parent)

					this.availability_date = response.data.availability_date
						? moment(response.data.availability_date)
								.tz('Etc/GMT')
								.format('YYYY-MM-DDTHH:mm:ss.SSS')
						: null
					this.category = response.data.trail_complete_category
					this.tag = response.data.tags
					this.profile = response.data.profiles
					this.area = response.data.areas
					this.user_group = response.data.user_groups

					this.mandatory_areas = response.data.mandatory_areas
					this.mandatory_profiles = response.data.mandatory_profiles

					this.image_url = response.data.trail_image

					if (response.data.trail_image_alt_texts) {
						const altTextMap = {}
						response.data.trail_image_alt_texts.forEach((altTextObj) => {
							altTextMap[altTextObj.language] = altTextObj.alt_text
						})
						this.language_br = altTextMap['pt-br'] || ''
						this.language_us = altTextMap['en-us'] || ''
						this.language_es = altTextMap['es-es'] || ''
						this.language_fr = altTextMap['fr-fr'] || ''
					}
				})
				.catch((e) => {
					console.log(e)
				})
				.finally(() => {
					this.$q.loading.hide()
				})
		},

		fetchTrailParents() {
			this.$q.loading.show()

			this.$axios
				.get(`/admin_trails/trails/${this.id_trail}/list_parent_trails/`, this.getHeader)
				.then((response) => {
					response.data.forEach((trail_parent) => {
						this.trail_parents.push({
							label: trail_parent.title,
							value: trail_parent.id,
						})
					})

					if (this.trail_parents.length) {
						this.parent_pagination.rowsNumber = this.trail_parents.length
						this.parent_pagination.rowsPerPage = this.trail_parents.length
					}

					this.trail_parents.sort((a, b) => b.value - a.value)

					if (this.id_trail != 0) this.fetchData()
					else this.$q.loading.hide()
				})
				.catch((e) => {
					this.$q.loading.hide()
					console.log(e)
				})
		},

		showTrailParents() {
			this.show_add_trail_parent = true
		},

		checkAll() {
			if (this.all_profiles) {
				this.profile = this.profiles.map((e) => e.value)
				this.changeProfiles()
			} else this.profile = []
		},

		checkAllAreas() {
			if (this.all_areas) {
				this.area = this.areas.map((e) => e.value)
				this.changeAreas()
			} else this.area = []
		},

		changeAreas() {
			this.$v.area.$touch
			this.mandatory_areas = this.mandatory_areas.filter((e) => this.area.includes(e))
		},

		changeProfiles() {
			this.$v.profile.$touch
			this.mandatory_profiles = this.mandatory_profiles.filter((e) =>
				this.profile.includes(e)
			)
		},

		checkAllMandatoryAreas() {
			if (this.all_mandatory_areas)
				this.mandatory_areas = this.mandatoryAreas.map((e) => e.value)
			else this.mandatory_areas = []

			this.$v.mandatory_areas.$touch
		},

		checkAllMandatoryProfiles() {
			if (this.all_mandatory_profiles)
				this.mandatory_profiles = this.mandatoryProfiles.map((e) => e.value)
			else this.mandatory_profiles = []

			this.$v.mandatory_profiles.$touch
		},

		getErrorColor(state) {
			if (state) return 'negative'
			else return ''
		},

		getErrorClass(state) {
			if (state) return 'checkbox-error'
			else return ''
		},

		getOrder(name) {
			if (name == 'cancel') {
				if (this.$q.platform.is.mobile) return 'order-last'
				else return 'order-first'
			} else if (name == 'save') {
				if (this.$q.platform.is.mobile) return 'order-first'
				else return 'order-last'
			}
		},

		imageUploaded(url) {
			this.image_url = url
		},

		selectParentTrail(id) {
			this.trail_parent = id
			const selectedParent = this.trail_parents.find((t) => t.value === id)
			if (selectedParent) {
				this.selected_parent_trail = [
					{ value: selectedParent.value, label: selectedParent.label },
				]
			}
			this.show_add_trail_parent = false
		},

		clearSelection() {
			this.trail_parent = ''
			this.selected_parent_trail = []
		},

		copyLink() {
			this.$copyText(this.url_trail_portal).then(
				(e) => {
					this.$q.notify({
						message: this.$t('link_copied_to_clipboard'),
						type: 'positive',
						icon: 'far fa-copy',
					})
				},
				(e) => {
					console.log(e)
				}
			)
		},
	},

	created() {
		this.id_trail = this.$route.params.id
		this.fetchTrailParents()
	},

	watch: {
		$route: function(refreshPage) {
			this.id_trail = this.$route.params.id
			this.fetchTrailParents()
		},
	},

	validations() {
		return {
			title: { required, maxLength: maxLength(256) },
			language_br: { maxLength: maxLength(256) },
			language_us: { maxLength: maxLength(256) },
			language_es: { maxLength: maxLength(256) },
			language_fr: { maxLength: maxLength(256) },
			description: { required, maxLength: maxLength(1024) },
			tag: { required },
			category: { 
				requiredIf: requiredIf(() => {
					return this.showCategories ? true : false
				}),
			},
			area: {
				requiredIf: requiredIf(() => {
					return this.tab == 'permissions' ? true : false
				}),
			},
			area: {
				requiredIf: requiredIf(() => {
					if (this.profile.length == 0 && this.area.length == 0) {
						if (
							this.profile.length == 0 &&
							this.area.length == 0 &&
							this.user_group.length == 0
						)
							return true
						else return false
					} else {
						return true
					}
				}),
			},
			profile: {
				requiredIf: requiredIf(() => {
					if (this.profile.length == 0 && this.area.length == 0) {
						if (
							this.profile.length == 0 &&
							this.area.length == 0 &&
							this.user_group.length == 0
						)
							return true
						else return false
					} else {
						return true
					}
				}),
			},
			user_group: {
				requiredIf: requiredIf(() => {
					if (
						this.profile.length == 0 &&
						this.area.length == 0 &&
						this.user_group.length == 0
					) {
						return true
					} else {
						return false
					}
				}),
			},
			mandatory_areas: {
				requiredIf: requiredIf(() => {
					return this.mandatory_profiles.length > 0
				}),
			},
			mandatory_profiles: {
				requiredIf: requiredIf(() => {
					return this.mandatory_areas.length > 0
				}),
			},
		}
	},
}
</script>

<style lang="stylus" scoped></style>
